import React from 'react';
import axios from 'axios';
import { Table, TableBody, TableRow, TableCell, TableHead, Collapse, IconButton, Box, CircularProgress, TextField} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';

function normalizeDebugState(state) {
    const nextState = {...state};
    const scooterStates = nextState.scooters ?? [];
    delete nextState.scooters;

    nextState.num_connections = nextState.proc_connections?.length;
    delete nextState.proc_connections;

    return {
        scooterStates,
        debugState: nextState,
    };
}

function renameBoltKeysForPublicDebug(value) {
    if(Array.isArray(value)) {
        return value.map(renameBoltKeysForPublicDebug);
    }

    if(value === null || value === undefined || typeof value !== 'object') {
        return value;
    }

    const renamed = {};

    Object.entries(value).forEach(([key, entryValue]) => {
        let nextKey = key;
        if(key === 'bolt_state') {
            nextKey = 'provider_state';
        }
        else if(key === 'bolt_state_updated_at') {
            nextKey = 'provider_state_updated_at';
        }
        else if(key === 'bolt_err') {
            nextKey = 'provider_err';
        }
        else if(key === 'bolt_request_dt_mean') {
            nextKey = 'provider_request_dt_mean';
        }
        else if(key.startsWith('bolt_stat_')) {
            nextKey = `provider_stat_${key.slice('bolt_stat_'.length)}`;
        }

        renamed[nextKey] = renameBoltKeysForPublicDebug(entryValue);
    });

    return renamed;
}

function selectedProvider() {
    const provider = new URLSearchParams(window.location.search).get('provider');
    if(!provider) {
        return null;
    }

    return provider.toLowerCase();
}

function filterDebugStateByProvider(state, provider) {
    if(!provider) {
        return state;
    }

    const filteredScooters = Object.fromEntries(
        Object.entries(state.scooters ?? {}).filter(([, scooterState]) => scooterState?.provider === provider)
    );

    const filteredLogs = (state.provider_backend_request_logs ?? []).filter(logEntry => {
        const requestBody = logEntry?.json_body;

        if(requestBody?.uuid && requestBody?.major !== undefined) {
            const scooterState = Object.values(filteredScooters).find(scooter => (
                scooter?.uuid === requestBody.uuid &&
                scooter?.major === requestBody.major &&
                scooter?.minor === requestBody.minor
            ));
            return Boolean(scooterState);
        }

        if(requestBody?.ibeacon) {
            const beacon = requestBody.ibeacon;
            const scooterState = Object.values(filteredScooters).find(scooter => (
                scooter?.uuid === beacon.uuid &&
                scooter?.major === beacon.major &&
                scooter?.minor === beacon.minor
            ));
            return Boolean(scooterState);
        }

        return false;
    });

    return {
        ...state,
        scooters: filteredScooters,
        provider_backend_request_logs: filteredLogs,
    };
}

function Row(props) {
    const {e} = props;
    const [open, setOpen] = React.useState(false);
    const area = e.provider_state ?? e.area ?? e.bolt_state;
    const areaName = area?.area_name ?? area?.city_name;
    const areaId = area?.city_id;

    const renderBeepGreen = new Date() - new Date(e.last_audible_beep_time * 1000) < 10000;

    return (<>
        <TableRow>
            <TableCell>
                {e.peer_states && (
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                )}
            </TableCell>
            <TableCell>
                0x{(e.minor | (e.major << 16)).toString(16).toUpperCase()} ({e.major} {e.minor})
            </TableCell>
            <TableCell>
                {areaName}{areaId !== undefined ? `(${areaId})` : ''}
            </TableCell>
            <TableCell style={{backgroundColor: (new Date() - new Date(e.updated_at * 1000) < 10000) ?  "lightgreen" : undefined}}>
                {new Date(e.updated_at * 1000).toLocaleString()}
            </TableCell>
            <TableCell style={{backgroundColor: (renderBeepGreen ?  "lightgreen" : undefined)}}>
                {e.last_audible_beep_time ? new Date(e.last_audible_beep_time * 1000).toLocaleString() : "-"} ({e.total_audible_beep_cnt})
            </TableCell>
            <TableCell style={{backgroundColor: (renderBeepGreen && e.intensity > 0) ?  "lightgreen" : undefined}}>
                {Number(e.intensity).toFixed(2)}
            </TableCell>
            <TableCell>
                {Number(e.intv).toFixed(2)}
            </TableCell>
            <TableCell>
                {Number(e.dist).toFixed(2)}
            </TableCell>
            {e.peer_states && (<>
              <TableCell>
                  {Object.keys(e.peer_states ?? {}).length}
              </TableCell>
              <TableCell>
                  {e.has_dangling_peer ? "D" : ""}
              </TableCell>
            </>)}
            {(e.flags !== null && e.flags !== undefined) && (
              <TableCell>
                  fl: 0x{e.flags.toString(16)}
              </TableCell>
            )}
        </TableRow>

        {e.peer_states && (
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, backgroundColor: 'lightgray' }}>
                            {/* <Typography variant="h6" gutterBottom component="div">
                                Peers
                            </Typography> */}
                            <Table size="small">
                                <TableBody>
                                    {Object.values(e.peer_states).map(s => {
                                        return (
                                            <Row e={s} />
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>

                        {/* <pre>
                            {JSON.stringify(e.peer_states, null, 4)}
                        </pre> */}
                    </Collapse>
                </TableCell>
            </TableRow>
        )}
    </>);
}

function ScooterDebugContent({loadState, showProviderRequestLogs = false, title = 'Scooter Backend Debug'}) {
    const [scooterStates, setScooterStates] = React.useState([]);
    const [debugState, setDebugState] = React.useState({});
    const [finishedInitialLoad, setFinishedInitialLoad] = React.useState(false);
    const timerRef = React.useRef()

    async function reload() {
        try {
            const state = await loadState();
            const normalizedState = normalizeDebugState(state);
            setScooterStates(normalizedState.scooterStates);
            setDebugState(normalizedState.debugState);
        }
        catch(err) {
            console.error("bolt_states.json fetch err", err);
        }
    }

    async function load() {
        await reload();
        setFinishedInitialLoad(true);

        if(timerRef.current) {
            clearInterval(timerRef.current);
        }

        timerRef.current = setInterval(() => {
            reload();
        }, 1000);
    }

    React.useEffect(() => {
        load();

        return () => {
            if(timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
      }, []);

    if(!finishedInitialLoad) {
        return (<CircularProgress />);
    }

    const providerRequestLogs = debugState.provider_backend_request_logs ?? [];
    const debugStateWithoutProviderLogs = {...debugState};
    delete debugStateWithoutProviderLogs.provider_backend_request_logs;

    return (<>
        <h1>{title}</h1>

        <Table aria-label="simple table">
            <TableHead>
                <TableRow>
                <TableCell></TableCell>
                <TableCell>id</TableCell>
                <TableCell>area</TableCell>
                <TableCell>updated</TableCell>
                <TableCell>last audible beep</TableCell>
                <TableCell>volume</TableCell>
                <TableCell>interval</TableCell>
                <TableCell>dist</TableCell>
                <TableCell>peers</TableCell>
                <TableCell>flags</TableCell>
                </TableRow>
            </TableHead>

            <TableBody>
                {Object.values(scooterStates ?? {}).map(e => {
                    return (<>
                        <Row key={e.major + e.minor} e={e} />
                    </>);
                })}
            </TableBody>
        </Table>

        {showProviderRequestLogs && (
            <>
                <h2 style={{marginTop: 48}}>Provider API Request Logs</h2>
                <TextField
                    value={JSON.stringify(providerRequestLogs, null, 4)}
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={24}
                    maxRows={24}
                    InputProps={{
                        readOnly: true,
                        style: {
                            fontFamily: 'monospace',
                            fontSize: 12,
                            lineHeight: 1.4,
                            overflow: 'auto',
                        },
                    }}
                />
            </>
        )}

        <pre style={{marginTop: 200}}>
            debugState: {JSON.stringify(debugStateWithoutProviderLogs, null, 4)}
        </pre>

        <pre style={{marginTop: 200}}>
            {JSON.stringify(scooterStates, null, 4)}
        </pre>

        <div style={{height: 240}} />
    </>
    )
}

export function ScooterDebugScreen({auth}) {
    return (
        <ScooterDebugContent
            loadState={async() => {
                const res = await axios.get(`/api/v1/scooter-engine-debug`, {
                    responseType: 'json'
                });
                console.log("get scooter states res:", res);
                return res.data;
            }}
        />
    );
}

export function ScooterDebugPublicLiveScreen() {
    const provider = selectedProvider();

    return (
        <ScooterDebugContent
            showProviderRequestLogs={true}
            title={provider ? `Scooter Backend Debug (${provider})` : 'Scooter Backend Debug'}
            loadState={async() => {
                const res = await axios.get(`/api/v1/scooter-engine-debug`, {
                    responseType: 'json'
                });
                console.log("get scooter states res:", res);
                const publicState = renameBoltKeysForPublicDebug(res.data);
                return filterDebugStateByProvider(publicState, provider);
            }}
        />
    );
}
